/**
 * System / chain / project types.
 *
 * A "system" is a complete signal chain: DAC + amp + output + room + gain.
 * Two systems (A and B) exist simultaneously so whole-chain comparison is a
 * crossfade, not a rebuild (spec 39).
 */

import type { HardwareCategory } from './hardware';

export type SlotId = 'dac' | 'amp' | 'output';

/**
 * Cable slots.
 *
 * A power cord belongs to a COMPONENT -- each box has its own inlet and its own
 * run to the wall, so the DAC and the amplifier get one each with independent
 * lengths. The other four are properties of the SYSTEM: one interconnect
 * between DAC and amplifier, one USB and one Ethernet feeding the DAC, and one
 * speaker cable from amplifier to loudspeaker.
 */
export type CableSlotId =
  | 'dacPower'
  | 'ampPower'
  | 'interconnect'
  | 'usb'
  | 'ethernet'
  | 'speakerCable';

export const CABLE_SLOTS: CableSlotId[] = [
  'dacPower',
  'ampPower',
  'interconnect',
  'usb',
  'ethernet',
  'speakerCable',
];

export const CABLE_SLOT_LABEL: Record<CableSlotId, string> = {
  dacPower: 'DAC power cord',
  ampPower: 'Amplifier power cord',
  interconnect: 'Interconnect',
  usb: 'USB',
  ethernet: 'Ethernet',
  speakerCable: 'Speaker cable',
};

/** Which cable category may occupy each slot. */
export const CABLE_SLOT_TYPE = {
  dacPower: 'power',
  ampPower: 'power',
  interconnect: 'interconnect',
  usb: 'usb',
  ethernet: 'ethernet',
  speakerCable: 'speaker',
} as const;

export interface CableSelection {
  profileId: string | null;
  /** Run length in metres. Free-form, not restricted to the maker's stock lengths. */
  lengthM: number;
}

export const DEFAULT_CABLE_LENGTH_M: Record<CableSlotId, number> = {
  dacPower: 1.5,
  ampPower: 1.5,
  interconnect: 1,
  usb: 1.5,
  ethernet: 1.5,
  speakerCable: 3,
};

export function makeDefaultCables(): Record<CableSlotId, CableSelection> {
  return {
    dacPower: { profileId: null, lengthM: 1.5 },
    ampPower: { profileId: null, lengthM: 1.5 },
    interconnect: { profileId: null, lengthM: 1 },
    usb: { profileId: null, lengthM: 1.5 },
    ethernet: { profileId: null, lengthM: 1.5 },
    speakerCable: { profileId: null, lengthM: 3 },
  };
}
export type SystemSlotKey = 'A' | 'B';

/** The four rapid-switch banks per slot (spec 90/91). */
export type BankIndex = 0 | 1 | 2 | 3;

export interface StageBypass {
  sourceGain: boolean;
  dacDigitalFilter: boolean;
  dacConversion: boolean;
  dacAnalog: boolean;
  interconnect: boolean;
  ampInput: boolean;
  ampNonlinear: boolean;
  ampOutput: boolean;
  speaker: boolean;
  room: boolean;
  spatial: boolean;
  listener: boolean;
  master: boolean;
}

export const DEFAULT_BYPASS: StageBypass = {
  sourceGain: false,
  dacDigitalFilter: false,
  dacConversion: false,
  dacAnalog: false,
  interconnect: false,
  ampInput: false,
  ampNonlinear: false,
  ampOutput: false,
  speaker: false,
  room: false,
  spatial: false,
  listener: false,
  master: false,
};

export type RoomPreset = 'none' | 'small' | 'medium' | 'large' | 'custom';

export interface RoomSettings {
  preset: RoomPreset;
  enabled: boolean;
  widthM: number;
  lengthM: number;
  heightM: number;
  /** Distance between the two speakers, metres. */
  speakerSpacingM: number;
  /** Listener distance from the speaker plane, metres. */
  listenerDistanceM: number;
  /** Listener lateral offset from centre line, metres. */
  listenerOffsetM: number;
  /** Distance from speakers to front wall, metres. */
  frontWallM: number;
  /** Distance from listener to rear wall, metres. */
  rearWallM: number;
  toeInDeg: number;
  /** Absorption coefficients 0..1 (1 = fully absorptive). */
  absorptionWalls: number;
  absorptionFloor: number;
  absorptionCeiling: number;
  /** Master wet/dry for the room stage. */
  mixPercent: number;
  /** Enable each reflection family. */
  floorReflection: boolean;
  ceilingReflection: boolean;
  sideReflection: boolean;
  rearReflection: boolean;
  /** Late-field decay time, seconds. */
  rt60: number;
  /** Optional imported room IR (IndexedDB key). */
  irId?: string;
}

export const DEFAULT_ROOM: RoomSettings = {
  preset: 'medium',
  enabled: false,
  widthM: 4.2,
  lengthM: 6.0,
  heightM: 2.6,
  speakerSpacingM: 2.4,
  listenerDistanceM: 2.8,
  listenerOffsetM: 0,
  frontWallM: 0.9,
  rearWallM: 1.4,
  toeInDeg: 12,
  absorptionWalls: 0.28,
  absorptionFloor: 0.18,
  absorptionCeiling: 0.22,
  mixPercent: 35,
  floorReflection: true,
  ceilingReflection: true,
  sideReflection: true,
  rearReflection: true,
  rt60: 0.42,
};

export type CrossfeedPreset = 'off' | 'low' | 'medium' | 'high' | 'custom';

export interface HeadphoneSettings {
  crossfeed: CrossfeedPreset;
  /** Custom crossfeed parameters. */
  crossfeedAmount: number;   // 0..1
  crossfeedDelayUs: number;  // interaural delay
  crossfeedLpHz: number;     // shadowing lowpass
  /** Seal / pad fit, -1 (loose) .. +1 (tight). Shifts LF. */
  seal: number;
  /** L/R trim in dB, applied post-headphone model. */
  balanceDb: number;
  /** User EQ bands applied after the headphone model. */
  eq: EqBand[];
  eqEnabled: boolean;
  /** Apply the headphone's declared target compensation. */
  targetCompensation: boolean;
}

export interface EqBand {
  id: string;
  type: 'peaking' | 'lowshelf' | 'highshelf' | 'lowpass' | 'highpass' | 'notch';
  freq: number;
  gainDb: number;
  q: number;
  enabled: boolean;
}

export const DEFAULT_HEADPHONE: HeadphoneSettings = {
  crossfeed: 'off',
  crossfeedAmount: 0.3,
  crossfeedDelayUs: 270,
  crossfeedLpHz: 700,
  seal: 0,
  balanceDb: 0,
  eq: [],
  eqEnabled: false,
  targetCompensation: false,
};

export interface SpatialSettings {
  enabled: boolean;
  /** Stereo width, 0 = mono, 1 = normal, 2 = wide. */
  width: number;
  /** Additional interchannel crosstalk in dB (negative = more separation). */
  crosstalkDb: number;
  /** Early-reflection level, dB. */
  earlyDb: number;
  /** Late-decay level, dB. */
  lateDb: number;
}

export const DEFAULT_SPATIAL: SpatialSettings = {
  enabled: false,
  width: 1,
  crosstalkDb: -90,
  earlyDb: -14,
  lateDb: -22,
};

export interface DacSettings {
  /** Selected reconstruction filter id from the DAC profile. */
  filterId?: string;
  /** Extra digital trim, dB. */
  trimDb: number;
  /** Use balanced output modelling. */
  balanced: boolean;
}

export interface AmpSettings {
  /** Volume position 0..1; maps into the amp gain model. */
  volume: number;
  trimDb: number;
  balancedInput: boolean;
}

export interface SystemChain {
  id: string;
  name: string;
  dacId: string | null;
  ampId: string | null;
  outputId: string | null;
  dac: DacSettings;
  amp: AmpSettings;
  room: RoomSettings;
  headphone: HeadphoneSettings;
  spatial: SpatialSettings;
  bypass: StageBypass;
  /** Cabling, including per-component power cords with independent lengths. */
  cables: Record<CableSlotId, CableSelection>;
  /** Per-chain output trim in dB (before master). */
  outputTrimDb: number;
}

export type RealismMode = 'standard' | 'high' | 'maximum';
export type PerformanceMode = 'low-latency' | 'balanced' | 'maximum-quality';
export type OversampleFactor = 1 | 2 | 4 | 8;

export interface EngineSettings {
  realism: RealismMode;
  performance: PerformanceMode;
  /** Manual override; 'auto' derives from realism + performance. */
  oversample: OversampleFactor | 'auto';
  /** Crossfade length for hardware transitions, ms (spec 14). */
  transitionMs: 5 | 10 | 25 | 50 | 100;
  /**
   * Recreate the AudioContext at the source file's sample rate on load.
   *
   * OFF by default, deliberately. Running the graph at the file's native rate
   * avoids one resampling stage, but it is the ONLY operation in the whole
   * application that destroys and rebuilds the audio graph, and creating an
   * AudioContext at a rate the audio device does not natively run at is slow
   * and, on some platforms, blocking. The browser and OS may resample at the
   * device boundary regardless -- which the engine reports honestly -- so the
   * gain is small and the risk is real. Users who want it can turn it on.
   */
  followSourceRate: boolean;
  /** Convolution partition size for the room/IR engine. */
  convolutionPartition: 128 | 256 | 512 | 1024;
  /** Enable WASM DSP kernels where validated. */
  useWasm: boolean;
  /** Experimental models (spec 53). */
  experimental: boolean;
  /** Safety limiter (spec 64). */
  safetyLimiter: boolean;
  masterGainDb: number;
  startupGainLimitDb: number;
  muted: boolean;
}

export const DEFAULT_ENGINE: EngineSettings = {
  // 'standard' is the default because it is the tier measured to run
  // comfortably in real time on ordinary hardware. 'high' and 'maximum' are
  // real options, but they are opt-in and the engine reports when a tier is
  // too heavy for the machine rather than quietly glitching.
  realism: 'standard',
  performance: 'balanced',
  oversample: 'auto',
  transitionMs: 25,
  followSourceRate: false,
  convolutionPartition: 256,
  useWasm: true,
  experimental: false,
  safetyLimiter: true,
  masterGainDb: -12,
  startupGainLimitDb: -6,
  muted: false,
};

export type LevelMatchMode = 'off' | 'peak' | 'rms' | 'lufs';

export interface LevelMatchSettings {
  mode: LevelMatchMode;
  /** Target level in dB for the chosen metric. */
  targetDb: number;
  /** Manual trim applied on top of automatic compensation, per system. */
  manualTrimA: number;
  manualTrimB: number;
  /** Force matching before blind tests (spec 94). */
  forceForBlind: boolean;
}

export const DEFAULT_LEVEL_MATCH: LevelMatchSettings = {
  mode: 'rms',
  targetDb: -20,
  manualTrimA: 0,
  manualTrimB: 0,
  forceForBlind: true,
};

export type ComparisonMode = 'single' | 'ab' | 'abx' | 'bank';

export interface AbxTrial {
  index: number;
  /** Which system X actually was. */
  truth: SystemSlotKey;
  /** What the listener chose. */
  choice: SystemSlotKey | null;
  correct: boolean | null;
  confidence: 1 | 2 | 3 | 4 | 5 | null;
  notes?: string;
  timestamp: number;
}

export interface SwitchEvent {
  timestamp: number;
  slot: SlotId | 'system' | 'bank' | 'cable';
  system: SystemSlotKey;
  fromId: string | null;
  toId: string | null;
  fromLabel: string;
  toLabel: string;
  transitionMs: number;
}

export interface SourceMetadata {
  fileName: string;
  fileSize: number;
  durationSec: number;
  sampleRate: number;
  bitDepth: number | null;
  channels: number;
  codec: string;
  container: string;
  lossless: boolean;
  bitrateKbps?: number;
  /** Tags read from the container, when present. */
  tags?: Record<string, string>;
}

export interface SourceAnalysis {
  peakDbFS: number;
  peakPerChannel: number[];
  truePeakDbTP: number | null;
  rmsDbFS: number;
  lufsIntegrated: number;
  lufsShortTermMax: number;
  loudnessRange: number | null;
  crestFactorDb: number;
  dynamicRangeDb: number;
  /** DC offset per channel. */
  dcOffset: number[];
  /** Inter-channel correlation, -1..1. */
  correlation: number;
  /** Estimated highest frequency with meaningful content, Hz. */
  spectralEdgeHz: number;
  /** Number of consecutive-sample clip events. */
  clipEvents: number;
  /** Histogram of sample values, for the "is this really 24-bit" check. */
  effectiveBits: number | null;
}

export interface ProjectFile {
  formatVersion: 1;
  name: string;
  created: string;
  modified: string;
  systemA: SystemChain;
  systemB: SystemChain;
  engine: EngineSettings;
  levelMatch: LevelMatchSettings;
  comparison: ComparisonMode;
  banks: Record<SlotId, (string | null)[]>;
  source?: {
    metadata: SourceMetadata;
    analysis: SourceAnalysis | null;
  };
  notes?: string;
}

export interface ChainSnapshot {
  id: string;
  label: string;
  timestamp: number;
  systemA: SystemChain;
  systemB: SystemChain;
  engine: EngineSettings;
  contextSampleRate: number;
  profileVersions: Record<string, number>;
}

export type CategoryForSlot = Record<SlotId, HardwareCategory[]>;
export const SLOT_CATEGORIES: CategoryForSlot = {
  dac: ['dac'],
  amp: ['amp'],
  output: ['speaker', 'headphone'],
};
